/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ApiResponse } from '@pbk-web/data';
import type { ClientTagResponse, ClientTagType, ClientUserGrouping, FCWithChildren, ModuleTemplate, ModuleType, Option, User } from '@pbk-web/types';
import type { ComponentType, ReactNode } from 'react';

export type UseCurrentClient = <T>(selector: (state: any) => T) => T;
export type UseCurrentUser = <T>(selector: (state: any) => T) => T;

export type AppEnv = {
  DataTable: FCWithChildren<any> & {
    Slot: any;
  };
  useFetchModules: (types: ModuleType[]) => { data: ModuleTemplate[] | undefined; isLoading: boolean };
  useFetchActivitiesGQL: <TTransformed extends object | null>(
    filter: any,
    pageNum: number,
    setLoading: (value: boolean | ((prev: boolean) => boolean)) => void,
    setData: (value: TTransformed | ((prev: TTransformed) => TTransformed)) => void,
    dataTransformer?: (res: ApiResponse<unknown[]> | null) => TTransformed,
  ) => readonly [(cursor: string | null) => Promise<void>, { current: string | null }];
  useFetchClientUsers: (includeDisabledUsers?: boolean) => { data: User[] | undefined; isLoading: boolean; isFetching: boolean };
  StaticBreadCrumb: FCWithChildren<{ breadCrumbs?: { name: string; path: string }[]; currentStepName: string }>;
  useFetchFormTemplatesConfiguration: (filter: any, disabled: boolean) => any;
  useFetchClientTemplateForms: (filter: any, disabled: boolean) => any;
  useCurrentUser: UseCurrentUser;
  useFetchClientGroups: (clientId?: string) => { data: ClientUserGrouping[]; isLoading: boolean; isFetching: boolean };
  usePermissions: () => (permissions: string | string[], clientIdOverride?: string) => boolean;
  InlineEditor: ComponentType<any>;
  ClientFormService: any;
  ClientTemplateFormService: any;
  FullscreenModal: ComponentType<any>;
  UsersSelect: ComponentType<any>;
  UserListRenderer: ComponentType<any>;
  /** The V1 "invite a new member" modal, injected so feature libs can reuse it. Props:
   *  `{ open; onClose; onInvited: (user: { id; firstName; lastName }) => void }`. */
  InviteMemberModal: ComponentType<any>;
  Groups: ComponentType<any>;
  useFetchClasses: () => () => Promise<ApiResponse<Array<{ clientModuleId: string; templateModule: ModuleTemplate }>>>;
  ClientTemplateModuleService: any;
  ModuleService: any;
  UploadArea: ComponentType<any>;
  MultiFileInfo: ComponentType<any>;
  FileService: any;
  FileUtils: any;
  FileUploadType: { Link: string; File: string } | undefined;
  FormRendererV2: ComponentType<any>;
  FormRendererMode: any;
  FormRendererType: any;
  ErrorPage: ComponentType<any>;
  DateFilterBase: ComponentType<any>;
  useStoredFilters: <TDefaults extends Record<string, Option<string | number, boolean>[]>>(
    pageId: string,
    defaults: TDefaults,
  ) => readonly [
    TDefaults,
    Record<
      string,
      (values: Option<string | number, boolean>[] | ((prev: Option<string | number, boolean>[]) => Option<string | number, boolean>[])) => void
    >,
    { clearFilters: () => void; hasFiltersApplied: boolean },
  ];
  useWarnOutboundLink: () => { onClick: (e: any) => void; modal: ReactNode };
  FormUtils: any;
  FormRendererDesignContext: any;
  FormRendererDesignDefaults: any;
  useFormAction: () => any;
  useFormRendererInfo: () => any;
  goToClientForm: (...args: any[]) => void;
  clientFormRoute: (...args: any[]) => string;
  useFetchClientTags: (types?: ClientTagType[], overrideClientId?: string) => { data: ClientTagResponse[] | undefined };
  convertToRichText: (text: string) => any;
  HtmlPreview: ComponentType<any>;

  /** Realtime for the forms v2 builder (presence + remote changes); wraps the builder page. */
  FormsV2BuilderRealtimeProvider: ComponentType<{ templateId: string; children: ReactNode }>;
  /** Attachment upload transport for the forms v2 builder (default answers); wraps the builder page. */
  FormsV2FileServiceProvider: ComponentType<{ children: ReactNode }>;

  useProcessesHub: () => { listenToProcess: <T extends Record<string, unknown>>(id: string, timeoutSeconds?: number) => Promise<T> };
};

let appEnv: AppEnv | null = null;

export function configureAppEnv(env: AppEnv) {
  appEnv = env;
}

export function getAppEnv(): AppEnv {
  if (!appEnv) {
    throw new Error('AppEnv not configured');
  }
  return appEnv;
}
